import { createContext, useCallback, useContext } from 'react';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Popover } from 'foundations-components/transitional/components/Popover';
import I18n from 'I18n';
import { VerticalMenuIcon } from 'foundations-assets/icons/components/VerticalMenuIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const FDTContextMenuConfigContext = /*#__PURE__*/createContext({});
export const ProvideFDTContextMenuConfig = ({
  contextMenuConfig,
  children
}) => {
  return /*#__PURE__*/_jsx(FDTContextMenuConfigContext.Provider, {
    value: {
      contextMenuConfig
    },
    children: children
  });
};
export const useFDTContextMenuConfig = () => {
  const {
    contextMenuConfig
  } = useContext(FDTContextMenuConfigContext);
  return contextMenuConfig;
};
export const useIsFDTContextMenuEnabled = () => {
  const contextMenuConfig = useFDTContextMenuConfig();
  return Boolean(contextMenuConfig);
};

/** Without this (or forcing the consumer to do it on every button within their ContextMenuContentComponent)
 * a click of any menu item will result in a click event bubbling up to the SortTH component, causing a "sort" event
 */
const StopPropogationContainer = ({
  children
}) => {
  const handleClick = useCallback(e => {
    e.stopPropagation();
  }, []);
  return /*#__PURE__*/_jsx("div", {
    onClick: handleClick,
    onContextMenu: handleClick,
    children: children
  });
};

/** When clicked will call `triggerContextMenu` with the columnNamespace, columnName, and row=null as arguments
 * Will conditionally render itself to not render when triggerContextMenu is not provided
 * Uses getIsContextMenuOpen to determine if the popover should be shown
 */
export const ContextMenuHeaderTriggerButton = ({
  columnNamespace,
  columnName
}) => {
  const contextMenuConfig = useFDTContextMenuConfig();
  const triggerContextMenu = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.triggerContextMenu;
  const ContextMenuContentComponent = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.ContextMenuContentComponent;
  const handleClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (triggerContextMenu) {
      triggerContextMenu({
        row: null,
        columnName,
        columnNamespace
      });
    }
  }, [triggerContextMenu, columnName, columnNamespace]);
  const onClose = useCallback(() => {
    if (triggerContextMenu) {
      triggerContextMenu({
        row: null,
        columnName: null,
        columnNamespace: null
      });
    }
  }, [triggerContextMenu]);
  if (!triggerContextMenu) {
    return null;
  }
  const content = /*#__PURE__*/_jsx(IconButton, {
    "aria-label": I18n.text('datatable.contextMenu.openContextMenu'),
    onClick: handleClick,
    size: "extra-small",
    use: "tertiary-transparent",
    children: /*#__PURE__*/_jsx(VerticalMenuIcon, {})
  });
  const isContextMenuOpen = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.getIsContextMenuOpen({
    row: null,
    columnName,
    columnNamespace
  });
  if (ContextMenuContentComponent && isContextMenuOpen) {
    return /*#__PURE__*/_jsx(Popover, {
      arrowSize: "none",
      closeOnOutsideClick: true,
      content: /*#__PURE__*/_jsx(StopPropogationContainer, {
        children: /*#__PURE__*/_jsx(ContextMenuContentComponent, {
          columnName: columnName,
          columnNamespace: columnNamespace,
          row: null
        })
      }),
      "data-test-id": "context-menu-popover"
      //using onOpenChange, combined with open=true, results in this acting like an `onClose` handler
      ,
      onOpenChange: onClose,
      open: true,
      placement: "right bottom",
      children: content
    });
  }
  return content;
};